import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Menu, RefreshCw, Search, SlidersHorizontal, Swords, X } from "lucide-react";
import { useAdminLayout } from "../AdminLayoutContext";

export default function AdminP2PChrome({
  search,
  onSearchChange,
  loading,
  onRefresh,
  filtersOpen,
  onFiltersToggle,
  activeFilterCount,
  summary,
  totalLoaded,
}) {
  const { openMobileMenu } = useAdminLayout();
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) onSearchChange("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  return (
    <>
      <header className="am-chrome">
        <div className="am-chrome-row">
          <div className="am-chrome-lead">
            <button
              type="button"
              className="am-chrome-btn am-chrome-btn-icon lg:hidden"
              onClick={openMobileMenu}
              aria-label="Open menu"
            >
              <Menu size={16} strokeWidth={2.2} />
            </button>

            <div className="am-chrome-brand" aria-label="P2P Desk">
              <Swords size={12} strokeWidth={2.3} aria-hidden />
              <span>P2P Desk</span>
            </div>

            <div className="am-chrome-stats" aria-label="P2P summary">
              <span className="am-chrome-stat tabular-nums">
                <em>{summary?.total ?? totalLoaded ?? "—"}</em> duels
              </span>
              <span className="am-chrome-stat am-chrome-stat-live tabular-nums">
                <em>{summary?.active ?? 0}</em> active
              </span>
              {(summary?.needsOutcome ?? 0) > 0 ? (
                <span className="am-chrome-stat am-chrome-stat-warn tabular-nums">
                  <em>{summary.needsOutcome}</em> pending outcome
                </span>
              ) : null}
            </div>
          </div>

          <div className="am-chrome-trail">
            <div
              ref={searchWrapRef}
              className={`am-chrome-search am-chrome-search-expand ${
                isExpanded ? "am-chrome-search-open" : ""
              } ${hasQuery ? "am-chrome-search-active" : ""}`}
            >
              {!isExpanded ? (
                <button
                  type="button"
                  className="am-chrome-search-toggle"
                  onClick={openSearch}
                  aria-label="Search duels"
                  aria-expanded={false}
                >
                  <Search size={14} strokeWidth={2.3} aria-hidden />
                </button>
              ) : (
                <>
                  <Search size={14} aria-hidden className="am-chrome-search-icon" />
                  <input
                    ref={inputRef}
                    type="search"
                    placeholder="Match, creator, selection…"
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onBlur={handleBlur}
                    aria-label="Search duels"
                    aria-expanded
                  />
                  {hasQuery ? (
                    <button
                      type="button"
                      onClick={() => onSearchChange("")}
                      aria-label="Clear search"
                      className="am-chrome-search-clear"
                    >
                      <X size={12} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={closeSearch}
                      aria-label="Close search"
                      className="am-chrome-search-clear"
                    >
                      <X size={12} />
                    </button>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              className={`am-chrome-btn am-chrome-btn-filters lg:hidden ${
                filtersOpen || activeFilterCount ? "am-chrome-btn-on" : ""
              }`}
              onClick={onFiltersToggle}
              aria-label="Filters"
            >
              <SlidersHorizontal size={15} strokeWidth={2.2} />
              {activeFilterCount ? <span className="am-chrome-badge">{activeFilterCount}</span> : null}
            </button>

            <button
              type="button"
              className="am-chrome-btn am-chrome-btn-icon"
              onClick={onRefresh}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              {loading ? (
                <Loader2 size={14} className="am-spin" aria-hidden />
              ) : (
                <RefreshCw size={14} strokeWidth={2.3} aria-hidden />
              )}
            </button>
          </div>
        </div>
      </header>
      <div className="am-chrome-spacer" aria-hidden />
    </>
  );
}
